import { useI18n } from '@/context/I18nContext';
import type { LapDiffMetric, LapDiffStats, LapDiffHoverInfo, LapDiffTimeDiff } from '@/lib/map/lapDiffMath';
import { LAP_COLORS } from '@/lib/map/lapDiffMath';
import { formatTime } from '@/lib/format';
import { X } from 'lucide-react';

interface LapInfo {
  sessionDate: string;
  sessionTime: string;
  lapNumber: number;
  lapTimeMs: number;
  color: string;
}

interface DiffLegendProps {
  lapA: LapInfo;
  lapB: LapInfo;
  metric: LapDiffMetric;
  onMetricChange: (metric: LapDiffMetric) => void;
  viewLap: 'A' | 'B';
  onViewLapChange: (lap: 'A' | 'B') => void;
  stats: LapDiffStats | null;
  hoverInfo: LapDiffHoverInfo | null;
  timeDiff: LapDiffTimeDiff | null;
  hasAnchors: boolean;
  onClearAnchors: () => void;
  labelA?: string;
  labelB?: string;
  showLapCards?: boolean;
}

const METRICS: { key: LapDiffMetric; labelKey: string }[] = [
  { key: 'time', labelKey: 'lapdiff.tabTime' },
  { key: 'speed', labelKey: 'lapdiff.tabSpeed' },
  { key: 'rpm', labelKey: 'lapdiff.tabRpm' },
  { key: 'acceleration', labelKey: 'lapdiff.tabAcceleration' },
];

export function DiffLegend({
  lapA, lapB, metric, onMetricChange, viewLap, onViewLapChange, stats, hoverInfo,
  timeDiff, hasAnchors, onClearAnchors, labelA, labelB, showLapCards = true,
}: DiffLegendProps) {
  const { t } = useI18n();
  const displayLabelA = labelA || t('lapdiff.viewLapA');
  const displayLabelB = labelB || t('lapdiff.viewLapB');

  return (
    <div className="flex flex-col gap-4 min-w-0">
      {/* Metric tabs */}
      <div className="p-1 bg-bg-surface border border-border-subtle rounded-xl flex gap-1 overflow-x-auto">
        {METRICS.map(m => (
          <button
            key={m.key}
            onClick={() => onMetricChange(m.key)}
            className={`flex-1 py-1.5 px-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
              metric === m.key
                ? 'bg-accent/15 text-accent shadow-xs border border-accent/30'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-transparent'
            }`}
          >
            {t(m.labelKey as any)}
          </button>
        ))}
      </div>

      {/* View lap toggle */}
      <div className="p-1 bg-bg-surface border border-border-subtle rounded-xl flex gap-1">
        <button
          onClick={() => onViewLapChange('A')}
          className={`flex-1 py-1.5 px-3 text-xs font-semibold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-2 ${
            viewLap === 'A'
              ? 'bg-bg-raised text-text-primary shadow-xs border border-border-subtle'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-transparent'
          }`}
        >
          <span
            className="w-2.5 h-2.5 rounded-full inline-block shrink-0"
            style={{ backgroundColor: lapA.color }}
          />
          <span className="truncate">{displayLabelA}</span>
        </button>
        <button
          onClick={() => onViewLapChange('B')}
          className={`flex-1 py-1.5 px-3 text-xs font-semibold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-2 ${
            viewLap === 'B'
              ? 'bg-bg-raised text-text-primary shadow-xs border border-border-subtle'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-transparent'
          }`}
        >
          <span
            className="w-2.5 h-2.5 rounded-full inline-block shrink-0"
            style={{ backgroundColor: lapB.color }}
          />
          <span className="truncate">{displayLabelB}</span>
        </button>
      </div>

      {/* Lap info cards */}
      {showLapCards && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-border-subtle bg-bg-surface/50">
            <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">{t('session.tabLaps')}</span>
          </div>
          <div className="divide-y divide-border-subtle">
            {[
              { label: 'A', info: lapA },
              { label: 'B', info: lapB },
            ].map(({ label, info }) => (
              <div key={label} className="flex items-center gap-3 px-4 py-2.5">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: info.color }}
                />
                <span className="text-xs font-bold text-text-secondary">{label}</span>
                <span className="text-xs text-text-muted flex-1 truncate">
                  {info.sessionDate} {info.sessionTime}
                </span>
                {info.lapNumber > 0 && (
                  <span className="text-xs font-semibold text-text-secondary">L{info.lapNumber}</span>
                )}
                <span className="mono text-xs font-semibold text-text-primary">
                  {formatTime(info.lapTimeMs)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time comparison card */}
      {timeDiff && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-border-subtle bg-bg-surface/50 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
              {timeDiff.segmentSpan
                ? t('lapdiff.segmentRange', { start: String(timeDiff.segmentSpan[0]), end: String(timeDiff.segmentSpan[1]) })
                : (timeDiff.isSegment ? t('lapdiff.segmentTime') : t('lapdiff.fullLap'))}
            </span>
            {hasAnchors && (
              <button
                onClick={onClearAnchors}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[0.65rem] font-medium text-text-muted bg-bg-surface border border-border-subtle hover:text-text-primary transition-colors cursor-pointer"
              >
                <X size={10} />
                {t('lapdiff.clearAnchors')}
              </button>
            )}
          </div>
          <div className="px-4 py-3">
            {/* Faster lap indicator */}
            <div className="text-center mb-3">
              {timeDiff.fasterLap === 'tie' ? (
                <span className="text-sm font-semibold text-text-secondary">
                  {t('lapdiff.equalTime')}
                </span>
              ) : (
                <span className="text-sm font-bold">
                  <span style={{ color: timeDiff.fasterLap === 'A' ? lapA.color : lapB.color }}>
                    {t('lapdiff.fasterBy', {
                      lap: timeDiff.fasterLap === 'A' ? displayLabelA : displayLabelB,
                      time: (timeDiff.diffMs / 1000).toFixed(2) + 's',
                    })}
                  </span>
                </span>
              )}
            </div>
            {/* Individual times */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: lapA.color }} />
                <span className="text-xs text-text-secondary">A:</span>
                <span className="mono text-xs font-semibold text-text-primary">
                  {formatTime(Math.round(timeDiff.lapATimeMs))}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: lapB.color }} />
                <span className="text-xs text-text-secondary">B:</span>
                <span className="mono text-xs font-semibold text-text-primary">
                  {formatTime(Math.round(timeDiff.lapBTimeMs))}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-border-subtle bg-bg-surface/50 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
              {t((METRICS.find(m => m.key === metric) ?? METRICS[0]).labelKey as any)}
            </span>
            <div className="flex items-center gap-2.5 text-[0.65rem] text-text-secondary">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: lapA.color }} />
                {displayLabelA}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: lapB.color }} />
                {displayLabelB}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-border-subtle">
            <div className="px-4 py-3 text-center">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted mb-1">
                {t('lapdiff.avgDiff')}
              </div>
              <div className="mono text-sm font-bold text-text-primary">
                {stats.avgDiff.toFixed(metric === 'rpm' ? 0 : 1)} {stats.unit}
              </div>
            </div>
            <div className="px-4 py-3 text-center">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted mb-1">
                {t('lapdiff.maxDiff')}
              </div>
              <div className="mono text-sm font-bold text-text-primary">
                {stats.maxDiff.toFixed(metric === 'rpm' ? 0 : 1)} {stats.unit}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-border-subtle border-t border-border-subtle">
            <div className="px-4 py-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: lapA.color }} />
              <span className="text-xs text-text-secondary">
                A: <span className="font-bold text-text-primary">{stats.pctABetter}%</span>{' '}
                {t('lapdiff.pctBetter')}
              </span>
            </div>
            <div className="px-4 py-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: lapB.color }} />
              <span className="text-xs text-text-secondary">
                B: <span className="font-bold text-text-primary">{stats.pctBBetter}%</span>{' '}
                {t('lapdiff.pctBetter')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Hover tooltip */}
      {hoverInfo && (
        <div className="card px-4 py-3">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted mb-2">
            {hoverInfo.segmentIndex !== undefined
              ? t('lapdiff.segmentDetail', { index: String(hoverInfo.segmentIndex) })
              : t('lapdiff.pointDetail')}
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: lapA.color }} />
              <span className="text-xs text-text-secondary">A:</span>
              <span className="mono text-xs font-bold text-text-primary">
                {hoverInfo.valueA.toFixed(metric === 'rpm' ? 0 : 1)} {hoverInfo.unit}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: lapB.color }} />
              <span className="text-xs text-text-secondary">B:</span>
              <span className="mono text-xs font-bold text-text-primary">
                {hoverInfo.valueB.toFixed(metric === 'rpm' ? 0 : 1)} {hoverInfo.unit}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-border-subtle">
              <span className="text-xs text-text-secondary">
                {metric === 'time' ? t('lapdiff.segmentDelta') : 'Δ'}:
              </span>
              <span
                className="mono text-xs font-bold"
                style={{
                  color:
                    metric === 'time'
                      ? (hoverInfo.valueA <= hoverInfo.valueB ? lapA.color : lapB.color)
                      : (hoverInfo.valueA >= hoverInfo.valueB ? lapA.color : lapB.color),
                }}
              >
                {hoverInfo.diff >= 0 ? '+' : ''}{hoverInfo.diff.toFixed(metric === 'rpm' ? 0 : 1)} {hoverInfo.unit}
              </span>
            </div>
            {hoverInfo.segmentTimeDiffMs !== undefined && metric !== 'time' && (
              <div className="flex items-center justify-between gap-2 pt-1 border-t border-border-subtle">
                <span className="text-xs text-text-secondary">{t('lapdiff.segmentDelta')}:</span>
                <span
                  className="mono text-xs font-bold"
                  style={{
                    color: (hoverInfo.segTimeAMs ?? 0) <= (hoverInfo.segTimeBMs ?? 0) ? lapA.color : lapB.color,
                  }}
                >
                  {hoverInfo.segmentTimeDiffMs >= 0 ? '+' : ''}{hoverInfo.segmentTimeDiffMs.toFixed(1)} ms
                </span>
              </div>
            )}
            {hoverInfo.cumulativeTimeDiffMs !== undefined && (
              <div className="flex items-center justify-between gap-2 pt-1 border-t border-border-subtle">
                <span className="text-xs text-text-secondary">{t('lapdiff.cumulativeDelta')}:</span>
                <span
                  className="mono text-xs font-bold"
                  style={{
                    color: (hoverInfo.cumTimeAMs ?? 0) <= (hoverInfo.cumTimeBMs ?? 0) ? lapA.color : lapB.color,
                  }}
                >
                  {hoverInfo.cumulativeTimeDiffMs >= 0 ? '+' : ''}{(hoverInfo.cumulativeTimeDiffMs / 1000).toFixed(2)}s
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

